import { useCallback, useEffect, useMemo, useState } from "react"
import { useLocalization } from "@tm/localization"
import { Alert, Badge, Box, Loader, styled, Tab, Tabs } from "@tm/components"
import { useCisReturnItemsTotalNumbers } from "@tm/context-distribution"
import { generatePath, Link, useParams } from "react-router-dom"
import { CisFunction, CisFunctionType, useShowCisOptions } from "@tm/data/cis"

type Props = {
    subpageRoute: string
}

type ComponentRouteProps = {
    subpage?: string
    voucherTypeId?: string
}

export const CisFunctionTabs = styled(Tabs)({
    "& .MuiTab-root": {
        margin: "7px 4px 4px 8px",
    },

    "& .MuiTabs-indicator": {
        bottom: "4px",
    },
})

export default function NavigationComponent({ subpageRoute }: Props) {
    const { translateText } = useLocalization()
    const { totalNumbers, totalNumbersLoading } = useCisReturnItemsTotalNumbers()
    const { cisOptions, cisOptionsLoading, cisOptionsLoaded, hasErrors } = useShowCisOptions()
    const { cisFunctions, errorMessage } = cisOptions || {}
    const matchParams = useParams<ComponentRouteProps>()

    const pageContext = useMemo(() => {
        if (matchParams.subpage === "vouchers") {
            return matchParams.subpage + matchParams.voucherTypeId
        }
        return matchParams.subpage
    }, [matchParams])
    const [value, setValue] = useState(pageContext)

    useEffect(() => {
        setValue(pageContext)
    }, [matchParams, pageContext])

    const getDescription = useCallback(
        (cisFuntion: CisFunction) => {
            switch (cisFuntion.functionType) {
                case CisFunctionType.Summary:
                    return translateText(409)
                case CisFunctionType.ReturnOfGoods:
                    return translateText(1515)
                case CisFunctionType.Invoices:
                    return translateText(656)
                case CisFunctionType.Backorders:
                    return translateText(13172)
                case CisFunctionType.DeliveryTours:
                    return translateText(789)
                case CisFunctionType.StatementOfCosts:
                    return translateText(13571)
                case CisFunctionType.Vouchers:
                    return cisFuntion.voucherType?.description
                default:
                    return ""
            }
        },
        [translateText]
    )

    const renderTab = useCallback(
        (cisFuntion: CisFunction) => {
            const { functionType, urlKey } = cisFuntion
            const voucherTypeId = functionType === CisFunctionType.Vouchers ? cisFuntion.voucherType.typeId : undefined
            const url = generatePath(subpageRoute, {
                ...matchParams,
                subpage: urlKey,
                voucherTypeId,
            })
            const keyValue = urlKey + (voucherTypeId ?? "")
            return (
                <Tab
                    component={Link}
                    key={keyValue}
                    value={keyValue}
                    icon={
                        functionType === CisFunctionType.ReturnOfGoods ? (
                            <Badge
                                size="small"
                                badgeContent={totalNumbersLoading ? <Loader size="extrasmall" /> : totalNumbers?.numberOfPositions}
                                sx={{ bottom: "12px" }}
                            />
                        ) : undefined
                    }
                    iconPosition="end"
                    label={getDescription(cisFuntion)}
                    to={decodeURIComponent(url)}
                    disableRipple
                />
            )
        },
        [getDescription, matchParams, subpageRoute, totalNumbers?.numberOfPositions, totalNumbersLoading]
    )

    /* wrab tabs component to avoid stretches, the active indicator will leave his position NEXT-23527 */
    return (
        <Box>
            {cisOptionsLoading && <Loader size="small" />}
            {(hasErrors || cisOptions?.hasErrors) && <Alert severity="error" title={`${translateText(1261)} ${errorMessage}`} />}
            {cisFunctions?.length && value && cisOptionsLoaded && (
                <CisFunctionTabs value={value} variant="scrollable">
                    {cisFunctions?.map((cisFunction) => renderTab(cisFunction))}
                </CisFunctionTabs>
            )}
        </Box>
    )
}
